import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QrCodeIcon, SearchIcon, UserIcon, WalletIcon, StarIcon, TagIcon, CheckCircleIcon, ShoppingCartIcon, MinusIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { formatVND, formatPhone, getTierNameVi } from '../../lib/formatters';
import { MOCK_CUSTOMERS, TIER_CONFIGS, MOCK_PROMOTIONS } from '../../lib/mockData';
import type { Customer } from '../../lib/types';
export function CheckoutLookupPage() {
  const [phone, setPhone] = useState('');
  const [foundCustomer, setFoundCustomer] = useState<Customer | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [useCredits, setUseCredits] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const handleSearch = () => {
    setIsSearching(true);
    setNotFound(false);
    setTimeout(() => {
      const cleaned = phone.replace(/\D/g, '');
      const customer = MOCK_CUSTOMERS.find(c => c.phone.includes(cleaned));
      if (customer) {
        setFoundCustomer(customer);
      } else {
        setNotFound(true);
        setFoundCustomer(null);
      }
      setIsSearching(false);
    }, 600);
  };
  const tierConfig = foundCustomer ? TIER_CONFIGS.find(t => t.level === foundCustomer.tier) : null;
  const activePromos = MOCK_PROMOTIONS.filter(p => p.status === 'active');
  const amount = parseInt(purchaseAmount.replace(/\D/g, ''), 10) || 0;
  const creditsToUse = foundCustomer && useCredits ? Math.min(foundCustomer.walletBalance, amount) : 0;
  const finalAmount = amount - creditsToUse;
  const pointsEarned = foundCustomer && tierConfig ? Math.floor(amount / 100000 * tierConfig.pointMultiplier) : 0;
  const handleConfirm = () => {
    setShowResult(true);
    setTimeout(() => setShowResult(false), 4000);
  };
  return <div className="max-w-2xl mx-auto px-4 py-8" data-id="element-1080">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1081">
        <h1 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-1082">
          <ShoppingCartIcon className="w-7 h-7 text-[#C8A84E]" data-id="element-1083" />
          Thanh Toán Thành Viên
        </h1>
      </motion.div>

      {/* Success Toast */}
      <AnimatePresence data-id="element-1084">
        {showResult && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-[#22C55E] text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2" data-id="element-1085">
            <CheckCircleIcon className="w-5 h-5" data-id="element-1086" />
            Thanh toán thành công! +{pointsEarned} điểm
          </motion.div>}
      </AnimatePresence>

      {/* Search */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.05
    }} data-id="element-1087">
        <Card className="mb-6" data-id="element-1088">
          <div className="flex gap-3" data-id="element-1089">
            <div className="flex-1" data-id="element-1090">
              <Input placeholder="Nhập số điện thoại khách hàng..." value={phone} onChange={e => {
              setPhone(e.target.value);
              setNotFound(false);
            }} leftIcon={<SearchIcon className="w-4 h-4" data-id="element-1092" />} onKeyDown={e => {
              if (e.key === 'Enter') handleSearch();
            }} data-id="element-1091" />
            </div>
            <Button onClick={handleSearch} isLoading={isSearching} className="flex-shrink-0" data-id="element-1093">
              Tra Cứu
            </Button>
          </div>
          <div className="flex items-center gap-3 mt-3" data-id="element-1094">
            <div className="h-px flex-1 bg-[#2A2A2A]" data-id="element-1095" />
            <span className="text-xs text-[#666666]" data-id="element-1096">hoặc</span>
            <div className="h-px flex-1 bg-[#2A2A2A]" data-id="element-1097" />
          </div>
          <button className="w-full mt-3 py-3 border border-dashed border-[#2A2A2A] rounded-lg text-sm text-[#666666] flex items-center justify-center gap-2 hover:border-[#C8A84E]/30 hover:text-[#B0B0B0] transition-colors" data-id="element-1098">
            <QrCodeIcon className="w-5 h-5" data-id="element-1099" />
            Quét mã QR thành viên
          </button>
          {notFound && <p className="text-sm text-[#EF4444] mt-2" data-id="element-1100">
              Không tìm thấy khách hàng với số điện thoại này
            </p>}
        </Card>
      </motion.div>

      {/* Customer Info */}
      <AnimatePresence data-id="element-1101">
        {foundCustomer && tierConfig && <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} data-id="element-1102">
            <Card className="mb-6" data-id="element-1103">
              <div className="flex items-center gap-4 mb-4" data-id="element-1104">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{
              backgroundColor: `${tierConfig.color}15`
            }} data-id="element-1105">
                  {tierConfig.icon}
                </div>
                <div className="flex-1" data-id="element-1106">
                  <h3 className="font-semibold text-white" data-id="element-1107">
                    {foundCustomer.name}
                  </h3>
                  <p className="text-sm text-[#B0B0B0]" data-id="element-1108">
                    {formatPhone(foundCustomer.phone)}
                  </p>
                </div>
                <Badge variant="tier" tier={foundCustomer.tier} size="md" data-id="element-1109">
                  {getTierNameVi(foundCustomer.tier)}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-1110">
                <div className="bg-[#141414] rounded-lg p-3 text-center" data-id="element-1111">
                  <WalletIcon className="w-4 h-4 text-[#C8A84E] mx-auto mb-1" data-id="element-1112" />
                  <p className="text-xs text-[#666666]" data-id="element-1113">Ví</p>
                  <p className="text-sm font-semibold text-white" data-id="element-1114">
                    {formatVND(foundCustomer.walletBalance)}
                  </p>
                </div>
                <div className="bg-[#141414] rounded-lg p-3 text-center" data-id="element-1115">
                  <StarIcon className="w-4 h-4 text-[#C8A84E] mx-auto mb-1" data-id="element-1116" />
                  <p className="text-xs text-[#666666]" data-id="element-1117">Điểm</p>
                  <p className="text-sm font-semibold text-white" data-id="element-1118">
                    {foundCustomer.points.toLocaleString('vi-VN')}
                  </p>
                </div>
              </div>

              {activePromos.length > 0 && <div className="bg-[#C8A84E]/5 border border-[#C8A84E]/10 rounded-lg p-3 mb-4" data-id="element-1119">
                  <p className="text-xs font-medium text-[#C8A84E] mb-1" data-id="element-1120">
                    Khuyến mãi đang áp dụng:
                  </p>
                  {activePromos.slice(0, 2).map(p => <p key={p.id} className="text-xs text-[#B0B0B0]" data-id="element-1121">
                      • {p.nameVi}
                    </p>)}
                </div>}

              {/* Purchase */}
              <div className="border-t border-[#2A2A2A] pt-4 space-y-3" data-id="element-1122">
                <Input label="Số tiền hóa đơn" placeholder="Ví dụ: 15000000" value={purchaseAmount} onChange={e => setPurchaseAmount(e.target.value)} rightIcon={<span className="text-sm text-[#666666]" data-id="element-1124">đ</span>} data-id="element-1123" />

                {amount > 0 && <>
                    <button onClick={() => setUseCredits(!useCredits)} className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${useCredits ? 'border-[#C8A84E] bg-[#C8A84E]/10' : 'border-[#2A2A2A]'}`} data-id="element-1125">
                      <span className="text-sm text-white" data-id="element-1126">
                        Sử dụng tín dụng ví
                      </span>
                      <div className={`w-10 h-6 rounded-full transition-colors ${useCredits ? 'bg-[#C8A84E]' : 'bg-[#2A2A2A]'}`} data-id="element-1127">
                        <div className={`w-5 h-5 bg-white rounded-full mt-0.5 transition-transform ${useCredits ? 'translate-x-4.5 ml-[18px]' : 'ml-0.5'}`} data-id="element-1128" />
                      </div>
                    </button>

                    <div className="bg-[#141414] rounded-lg p-4 space-y-2" data-id="element-1129">
                      <div className="flex justify-between text-sm" data-id="element-1130">
                        <span className="text-[#B0B0B0]" data-id="element-1131">Hóa đơn</span>
                        <span className="text-white" data-id="element-1132">{formatVND(amount)}</span>
                      </div>
                      {creditsToUse > 0 && <div className="flex justify-between text-sm" data-id="element-1133">
                          <span className="text-[#B0B0B0]" data-id="element-1134">Tín dụng ví</span>
                          <span className="text-[#22C55E]" data-id="element-1135">
                            -{formatVND(creditsToUse)}
                          </span>
                        </div>}
                      <div className="border-t border-[#2A2A2A] pt-2 flex justify-between" data-id="element-1136">
                        <span className="text-sm font-semibold text-white" data-id="element-1137">
                          Còn phải trả
                        </span>
                        <span className="text-lg font-bold text-[#C8A84E]" data-id="element-1138">
                          {formatVND(Math.max(0, finalAmount))}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-[#666666] pt-1" data-id="element-1139">
                        <span data-id="element-1140">Điểm nhận được</span>
                        <span className="text-[#C8A84E]" data-id="element-1141">
                          +{pointsEarned} điểm
                        </span>
                      </div>
                    </div>

                    <Button fullWidth onClick={handleConfirm} data-id="element-1142">
                      <CheckCircleIcon className="w-4 h-4" data-id="element-1143" />
                      Xác Nhận Thanh Toán
                    </Button>
                  </>}
              </div>
            </Card>
          </motion.div>}
      </AnimatePresence>
    </div>;
}